import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from './components/sidebar'
import Footer from './components/footer'
import 'react-quill/dist/quill.snow.css';

// Import icons from react-icons
import { 
  FaFileAlt, FaEye, FaHotel, FaList, FaBoxOpen, FaBoxes, 
  FaEdit, FaNewspaper, FaUsers, FaChartLine, FaDatabase, 
  FaCalendar, FaClock, FaCheckCircle, FaExclamationTriangle, 
  FaStar, FaMoneyBillWave, FaGlobe, FaCloudSun, FaBell,
  FaChartBar, FaTachometerAlt, FaShieldAlt, FaCog,
  FaEnvelope, FaCalendarAlt, FaUserPlus, FaDownload, FaRocket,
  FaUser, FaArrowUp, FaArrowDown, FaSun, FaMoon, FaThermometerHalf
} from 'react-icons/fa';

export default function Main() {
  const [timeZones, setTimeZones] = useState({
    pakistan: { time: '', date: '' },
    saudia: { time: '', date: '' },
    uk: { time: '', date: '' }
  });
  const [greeting, setGreeting] = useState('');
  const [stats, setStats] = useState({
    seoCount: 0,
    hotelCount: 0,
    packageCount: 0,
    blogCount: 0,
    totalRevenue: 0,
    activeUsers: 0,
    recentActivity: [],
    systemStatus: 'Optimal',
    weather: {
      pakistan: { temp: 25, condition: 'Cloudy' },
      saudia: { temp: 32, condition: 'Sunny' },
      uk: { temp: 12, condition: 'Cloudy' }
    }
  });

  // Live clock for multiple time zones
  useEffect(() => {
    const updateTimeZones = () => {
      const now = new Date();
      
      // Pakistan Time (UTC+5)
      const pakistanTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Karachi' }));
      const pakistanDateString = pakistanTime.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      const pakistanTimeString = pakistanTime.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit',
        second: '2-digit' 
      });
      
      // Saudi Arabia Time (UTC+3)
      const saudiaTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Riyadh' }));
      const saudiaDateString = saudiaTime.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      const saudiaTimeString = saudiaTime.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit',
        second: '2-digit' 
      });
      
      // UK Time (UTC+0/1 depending on DST)
      const ukTime = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/London' }));
      const ukDateString = ukTime.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      const ukTimeString = ukTime.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit',
        second: '2-digit' 
      });
      
      setTimeZones({
        pakistan: { 
          time: `${pakistanDateString} | ${pakistanTimeString}`,
          date: pakistanDateString
        },
        saudia: { 
          time: `${saudiaDateString} | ${saudiaTimeString}`,
          date: saudiaDateString
        },
        uk: { 
          time: `${ukDateString} | ${ukTimeString}`,
          date: ukDateString
        }
      });
      
      // Set greeting based on Pakistan time (or you can use local time)
      const hour = pakistanTime.getHours();
      if (hour < 12) setGreeting('Good Morning');
      else if (hour < 18) setGreeting('Good Afternoon');
      else setGreeting('Good Evening');
    };
    
    updateTimeZones();
    const interval = setInterval(updateTimeZones, 1000);
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      // Mock data - replace with actual API calls
      setStats({
        seoCount: 24,
        hotelCount: 15,
        packageCount: 8,
        blogCount: 12,
        totalRevenue: 152400,
        activeUsers: 342,
        recentActivity: [
          { id: 1, action: 'New SEO page added for Hajj 2024', time: '2 hours ago', type: 'seo', user: 'Admin' },
          { id: 2, action: 'Hotel "Grand Makkah" updated with new images', time: '5 hours ago', type: 'hotel', user: 'Manager' },
          { id: 3, action: 'Package "Hajj Premium" created successfully', time: '1 day ago', type: 'package', user: 'Admin' },
          { id: 4, action: 'New blog post published about Umrah tips', time: '2 days ago', type: 'blog', user: 'Editor' },
          { id: 5, action: 'System backup completed', time: '3 days ago', type: 'system', user: 'System' },
        ],
        systemStatus: 'Optimal',
        weather: {
          pakistan: { temp: 25, condition: 'Cloudy' },
          saudia: { temp: 32, condition: 'Sunny' },
          uk: { temp: 12, condition: 'Cloudy' }
        }
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Chart data - would be populated from API
  const revenueData = [
    { month: 'Jan', revenue: 4000 },
    { month: 'Feb', revenue: 3000 },
    { month: 'Mar', revenue: 5000 },
    { month: 'Apr', revenue: 8000 },
    { month: 'May', revenue: 15000 },
    { month: 'Jun', revenue: 12000 },
  ];

  const trafficData = [
    { name: 'Direct', value: 400 },
    { name: 'Social', value: 300 },
    { name: 'Search', value: 300 },
    { name: 'Referral', value: 200 },
  ];

  const COLORS = ['#059e98', '#8B5CF6', '#F59E0B', '#EC4899'];

  // Dashboard boxes
  const boxes = [
    { 
      title: "New SEO Page", 
      color: "#C99A3A",
      gradient: "linear-gradient(135deg, #C99A3A 0%, #E6B325 100%)",
      link: "/addseo",
      icon: <FaFileAlt size={24} />,
      count: stats.seoCount,
      description: 'Optimize content',
      badge: 'SEO'
    },
    { 
      title: "View SEO", 
      color: "#0FA5A0",
      gradient: "linear-gradient(135deg, #0FA5A0 0%, #059e98 100%)",
      link: "/viewseo",
      icon: <FaEye size={24} />,
      count: stats.seoCount,
      description: 'Manage pages',
      badge: 'Monitor'
    },
    { 
      title: "New Hotel", 
      color: "#8B5CF6",
      gradient: "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)",
      link: "/addhotel",
      icon: <FaHotel size={24} />,
      count: stats.hotelCount,
      description: 'Add properties',
      badge: 'Hotel'
    },
    { 
      title: "Hotels List", 
      color: "#EF4444",
      gradient: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
      link: "/viewhotel",
      icon: <FaList size={24} />,
      count: stats.hotelCount,
      description: 'View all hotels',
      badge: 'Manage'
    },
    { 
      title: "New Packages", 
      color: "#F59E0B",
      gradient: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
      link: "/addpackage",
      icon: <FaBoxOpen size={24} />,
      count: stats.packageCount,
      description: 'Create offers',
      badge: 'Package'
    },
    { 
      title: "Package List", 
      color: "#06B6D4",
      gradient: "linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)",
      link: "/listpackage",
      icon: <FaBoxes size={24} />,
      count: stats.packageCount,
      description: 'Browse packages',
      badge: 'View'
    },
    { 
      title: "New Blogs", 
      color: "#1E293B",
      gradient: "linear-gradient(135deg, #1E293B 0%, #0F172A 100%)",
      link: "/add-blogs",
      icon: <FaEdit size={24} />,
      count: stats.blogCount,
      description: 'Write content',
      badge: 'Blog'
    },
    { 
      title: "All Blogs", 
      color: "#EC4899",
      gradient: "linear-gradient(135deg, #EC4899 0%, #DB2777 100%)",
      link: "/all-blogs",
      icon: <FaNewspaper size={24} />,
      count: stats.blogCount,
      description: 'Manage posts',
      badge: 'Content'
    }
  ];

  // Quick Stats Cards
  const quickStats = [
    { 
      title: 'Total SEO Pages', 
      value: stats.seoCount, 
      icon: <FaFileAlt size={22} />, 
      color: '#059e98', 
      change: '+2', 
      trend: 'up',
      percentage: '8%'
    },
    { 
      title: 'Total Hotels', 
      value: stats.hotelCount, 
      icon: <FaHotel size={22} />, 
      color: '#059e98', 
      change: '+1', 
      trend: 'up',
      percentage: '5%'
    },
    { 
      title: 'Total Packages', 
      value: stats.packageCount, 
      icon: <FaBoxes size={22} />, 
      color: '#059e98', 
      change: '+3', 
      trend: 'up',
      percentage: '15%'
    },
    { 
      title: 'Total Revenue', 
      value: `$${(stats.totalRevenue / 1000).toFixed(1)}K`, 
      icon: <FaMoneyBillWave size={22} />, 
      color: '#059e98', 
      change: '+12.5%', 
      trend: 'up',
      percentage: '12.5%'
    },
  ];

  // System Metrics
  const systemMetrics = [
    { label: 'CPU Usage', value: 65, color: '#059e98', icon: <FaTachometerAlt /> },
    { label: 'Memory', value: 42, color: '#8B5CF6', icon: <FaDatabase /> },
    { label: 'Storage', value: 78, color: '#F59E0B', icon: <FaDatabase /> },
    { label: 'Bandwidth', value: 35, color: '#EC4899', icon: <FaGlobe /> },
  ];

  return (
    <>
      <div className="main-wrapper">
        <Sidebar />
        <div className="page-wrapper">
          
          <div className="page-content">
            {/* Advanced Dashboard Header with Clock and Weather */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card modern-card border-0 overflow-hidden" style={{ 
                  background: 'linear-gradient(135deg, #059e98 0%, #ca964c 100%)',
                  borderRadius: '16px'
                }}>
                  <div className="card-body py-4 px-5 text-white">
                    <div className="row align-items-center">
                      <div className="col-lg-8">
                        <div className="d-flex align-items-center mb-3">
                          <div className="avatar avatar-lg me-3 bg-white rounded-circle d-flex align-items-center justify-content-center" style={{ color: "#059e98" }}>
                            <FaChartLine size={24} />
                          </div>
                          <div>
                            <h2 className="mb-1" style={{ fontWeight: '700' }}>{greeting}, Admin!</h2>
                            <p className="mb-0 opacity-90">Welcome to your Tawaf Travel website management dashboard</p>
                          </div>
                        </div>
                        
                      {/* Pakistan Time and Weather */}
<div style={{ marginBottom: '12px' }}>
  <div
    style={{
      display: 'flex',
      alignItems: 'stretch',
      background: '#ffffff',
      borderRadius: '8px',
      border: '1px solid rgba(0,0,0,0.1)',
      overflow: 'hidden',
      width: 'fit-content',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    }}
  >
    {/* Country Badge - Pakistan */}
    <div
      style={{
        padding: '6px 8px',
        background: 'linear-gradient(135deg, #46835a 70%, #fcfcfc 100%)',
        color: '#fff',
        fontSize: '0.85rem',
        fontWeight: 700,
        letterSpacing: '0.5px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      PAK
    </div>

    {/* Content */}
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '6px 8px',
        gap: '8px',
        flexShrink: 0,
      }}
    >
      {/* Time */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          flexShrink: 0,
        }}
      >
        <FaClock size={14} color="#2563eb" />
        <span
          style={{
            fontSize: '0.85rem',
            fontFamily: 'monospace',
            color: '#1f2937',
            whiteSpace: 'nowrap',
          }}
        >
          {timeZones.pakistan.time}
        </span>
      </div>

      {/* Vertical Divider */}
      <div
        style={{
          width: '1px',
          height: '18px',
          background: 'rgba(0,0,0,0.15)',
        }}
      />

      {/* Weather */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          flexShrink: 0,
        }}
      >
        <FaCloudSun size={14} color="#f59e0b" />
        <span style={{ 
          fontSize: '0.85rem', 
          fontWeight: 700, 
          color: '#1f2937', 
          whiteSpace: 'nowrap' 
        }}>
          {stats.weather.pakistan.temp}°C
        </span>
        <span
          style={{
            fontSize: '0.8rem',
            color: '#6b7280',
            whiteSpace: 'nowrap',
          }}
        >
          {stats.weather.pakistan.condition}
        </span>
      </div>
    </div>
  </div>
</div>

{/* Saudi Arabia Time and Weather */}
<div style={{ marginBottom: '12px' }}>
  <div
    style={{
      display: 'flex',
      alignItems: 'stretch',
      background: '#ffffff',
      borderRadius: '8px',
      border: '1px solid rgba(0,0,0,0.1)',
      overflow: 'hidden',
      width: 'fit-content',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    }}
  >
    {/* Country Badge - Saudi Arabia */}
    <div
      style={{
        padding: '6px 8px',
        background: 'linear-gradient(135deg, #005430 70%, #eaf0ee 100%)',
        color: '#fff',
        fontSize: '0.85rem',
        fontWeight: 700,
        letterSpacing: '0.5px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      KSA
    </div>

    {/* Content */}
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '6px 8px',
        gap: '8px',
        flexShrink: 0,
      }}
    >
      {/* Time */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          flexShrink: 0,
        }}
      >
        <FaClock size={14} color="#2563eb" />
        <span
          style={{
            fontSize: '0.85rem',
            fontFamily: 'monospace',
            color: '#1f2937',
            whiteSpace: 'nowrap',
          }}
        >
          {timeZones.saudia.time}
        </span>
      </div>

      {/* Vertical Divider */}
      <div
        style={{
          width: '1px',
          height: '18px',
          background: 'rgba(0,0,0,0.15)',
        }}
      />

      {/* Weather */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          flexShrink: 0,
        }}
      >
        <FaCloudSun size={14} color="#f59e0b" />
        <span style={{ 
          fontSize: '0.85rem', 
          fontWeight: 700, 
          color: '#1f2937', 
          whiteSpace: 'nowrap' 
        }}>
          {stats.weather.saudia.temp}°C
        </span>
        <span
          style={{
            fontSize: '0.8rem',
            color: '#6b7280',
            whiteSpace: 'nowrap',
          }}
        >
          {stats.weather.saudia.condition}
        </span>
      </div>
    </div>
  </div>
</div>

{/* UK Time and Weather */}
<div
  style={{
    display: 'flex',
    alignItems: 'stretch',
    background: '#ffffff',
    borderRadius: '8px',
    border: '1px solid rgba(0,0,0,0.1)',
    overflow: 'hidden',
    width: 'fit-content',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  }}
>
  {/* Country Badge - UK */}
  <div
    style={{
      padding: '6px 8px',
      background: 'linear-gradient(135deg, #012066 0%, #c20f2d 50%, #f7f7f7 100%)',
      color: '#fff',
      fontSize: '0.85rem',
      fontWeight: 700,
      letterSpacing: '0.5px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    UK
  </div>

  {/* Content */}
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      padding: '6px 8px',
      gap: '8px',
      flexShrink: 0,
    }}
  >
    {/* Time */}
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        flexShrink: 0,
      }}
    >
      <FaClock size={14} color="#2563eb" />
      <span
        style={{
          fontSize: '0.85rem',
          fontFamily: 'monospace',
          color: '#1f2937',
          whiteSpace: 'nowrap',
        }}
      >
        {timeZones.uk.time}
      </span>
    </div>

    {/* Vertical Divider */}
    <div
      style={{
        width: '1px',
        height: '18px',
        background: 'rgba(0,0,0,0.15)',
      }}
    />

    {/* Weather */}
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        flexShrink: 0,
      }}
    >
      <FaCloudSun size={14} color="#f59e0b" />
      <span style={{ 
        fontSize: '0.85rem', 
        fontWeight: 700, 
        color: '#1f2937', 
        whiteSpace: 'nowrap' 
      }}>
        {stats.weather.uk.temp}°C
      </span>
      <span
        style={{
          fontSize: '0.8rem',
          color: '#6b7280',
          whiteSpace: 'nowrap',
        }}
      >
        {stats.weather.uk.condition}
      </span>
    </div>
  </div>
</div>
                        
                        <div className="d-flex align-items-center gap-3 mt-3">
                          <span className="badge bg-white text-dark py-2 px-3 d-flex align-items-center">
                            <FaCheckCircle className="me-2 text-success" />
                            System: {stats.systemStatus}
                          </span>
                          <span className="badge bg-white text-dark bg-opacity-20 py-2 px-3">
                            <FaUsers className="me-2 text-success" />
                            {stats.activeUsers} Active Users
                          </span>
                          <span className="badge bg-white text-dark bg-opacity-20 py-2 px-3">
                            <FaBell className="me-2 text-success" />
                            3 Notifications
                          </span>
                        </div>
                      </div>
                      
                      <div className="col-lg-4 text-lg-end mt-3 mt-lg-0">
                        <div className="card bg-white bg-opacity-40 border-0 p-3" style={{ backdropFilter: 'blur(10px)' }}>
                          <h5 className="mb-2 fw-bold" style={{ color: "#111212ff" }}>
                            <FaCalendarAlt className="me-2" />
                            Today's Summary
                          </h5>
                          <div className="row g-2 text-center">
                            <div className="col-6">
                              <div  className="p-2 rounded border  border-opacity-30"
                                style={{ backgroundColor: '#059e98' }}
                              >
                                <div className="fw-bold text-white" style={{ fontSize: '1.25rem' }}>12</div>
                                <small className="text-white text-opacity-80">
                                  <FaEye className="me-1" size={10} />
                                  Visitors
                                </small>
                              </div>
                            </div>
                            <div className="col-6">
                              <div className="p-2 bg-success bg-opacity-20 rounded border  border-opacity-30">
                                <div className="fw-bold text-white" style={{ fontSize: '1.25rem' }}>8</div>
                                <small className="text-white text-opacity-80">
                                  <FaCalendar className="me-1" size={10} />
                                  Bookings
                                </small>
                              </div>
                            </div>
                            <div className="col-6">
                              <div className="p-2 bg-info bg-opacity-20 rounded border  border-opacity-30">
                                <div className="fw-bold text-white" style={{ fontSize: '1.25rem' }}>3</div>
                                <small className="text-white text-opacity-80">
                                  <FaEnvelope className="me-1" size={10} />
                                  Messages
                                </small>
                              </div>
                            </div>
                            <div className="col-6">
                              <div  className="p-2 rounded border  border-opacity-30"
                                style={{ backgroundColor: '#ca964c' }}
                              >
                                <div className="fw-bold text-white" style={{ fontSize: '1.25rem' }}>95%</div>
                                <small className="text-white text-opacity-80">
                                  <FaStar className="me-1" size={10} />
                                  Satisfaction
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats Cards with Trends */}
            <div className="row g-3 mb-4">
              {quickStats.map((stat, index) => (
                <div key={index} className="col-xl-3 col-lg-3 col-md-6 col-sm-6">
                  <div className="card modern-card border-0 shadow-sm">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                          <h6 className="text-muted mb-1">{stat.title}</h6>
                          <h2 className="mb-0" style={{ color: stat.color, fontWeight: '700' }}>{stat.value}</h2>
                        </div>
                        <div className="avatar avatar-sm" style={{ 
                          backgroundColor: stat.color + '20', 
                          color: stat.color,
                          width: '48px',
                          height: '48px'
                        }}>
                          {stat.icon}
                        </div>
                      </div>
                      <div className="d-flex align-items-center">
                        <span   className="badge me-2"
                          style={{
                            background: stat.trend === 'up'
                              ? 'linear-gradient(135deg, #059e98 50%, #ca964c 100%)'
                              : '#dc3545',
                            color: 'white'
                          }}
                        >
                          {stat.trend === 'up' ? <FaArrowUp className="me-1" /> : <FaArrowDown className="me-1" />}
                          {stat.change}
                        </span>
                        <span className="text-muted small">vs last month</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Main Content Area */}
            <div className="row mb-4">
              {/* Left Column - Quick Actions */}
              <div className="col-lg-8">
                <div className="card modern-card border-0 shadow-sm">
                  <div className="card-header bg-transparent border-0 pb-0">
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="mb-0 d-flex align-items-center">
                        <FaRocket className="me-2" style={{ color: '#059e98' }} />
                        Quick Actions
                      </h5>
                      <span className="badge" style={{ background: '#059e98' }}>8 Modules</span>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="row g-3">
                      {boxes.map((box, index) => (
                        <div key={index} className="col-xl-3 col-lg-4 col-md-6 col-sm-6">
                          <a
                            href={box.link}
                            className="card text-white text-decoration-none border-0 shadow-sm"
                            style={{
                              padding: "20px 15px",
                              borderRadius: "12px",
                              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              minHeight: "140px",
                              background: box.gradient,
                              position: 'relative',
                              overflow: 'hidden'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "translateY(-8px) scale(1.02)";
                              e.currentTarget.style.boxShadow = "0 20px 40px rgba(0, 0, 0, 0.15)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "translateY(0) scale(1)";
                              e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.1)";
                            }}
                          >
                            {/* Animated Background Effect */}
                            <div className="position-absolute top-0 start-0 w-100 opacity-10"
                              style={{
                                background: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.8) 0%, transparent 50%)'
                              }}
                            ></div>
                            
                            {/* Icon with Glow Effect */}
                            <div 
                              className="mb-3 position-relative"
                              style={{
                                transition: "transform 0.3s ease"
                              }}
                            >
                              <div className="position-absolute top-50 start-50 translate-middle"
                                style={{
                                  width: '60px',
                                  height: '60px',
                                  background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)',
                                  borderRadius: '50%'
                                }}
                              ></div>
                              {box.icon}
                            </div>
                            
                            {/* Title and Description */}
                            <h6 className="card-title m-0 fw-bold text-center" style={{ fontSize: '0.95rem' }}>
                              {box.title}
                            </h6>
                            <small className="mt-2 opacity-90 text-center" style={{ fontSize: '0.8rem' }}>
                              {box.description}
                            </small>
                            
                            {/* Top Right Badge */}
                            <div className="position-absolute top-0 end-0 m-2">
                              <span className="badge bg-dark bg-opacity-50 px-2 py-1" style={{ fontSize: '0.7rem' }}>
                                {box.badge}
                              </span>
                            </div>
                            
                            {/* Bottom Count */}
                            {box.count > 0 && (
                              <div className="position-absolute bottom-0 start-0 m-2">
                                <span className="badge bg-white text-dark px-2 py-1 fw-bold">
                                  {box.count}
                                </span>
                              </div>
                            )}
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* System Metrics Row */}
                <div className="row g-3 mt-3">
                  {systemMetrics.map((metric, index) => (
                    <div key={index} className="col-lg-3 col-md-6">
                      <div className="card modern-card border-0 shadow-sm">
                        <div className="card-body">
                          <div className="d-flex align-items-center mb-3">
                            <div className="avatar avatar-sm me-2" style={{ 
                              backgroundColor: metric.color + '20', 
                              color: metric.color 
                            }}>
                              {metric.icon}
                            </div>
                            <span className="text-muted">{metric.label}</span>
                          </div>
                          <div className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0" style={{ color: metric.color }}>{metric.value}%</h4>
                            <div className="progress flex-grow-1 ms-3" style={{ height: '6px' }}>
                              <div 
                                className="progress-bar" 
                                role="progressbar" 
                                style={{ 
                                  width: `${metric.value}%`, 
                                  backgroundColor: metric.color,
                                  borderRadius: '3px'
                                }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Column - Activity & Stats */}
              <div className="col-lg-4">
                {/* Recent Activity Timeline */}
                <div className="card modern-card h-100 border-0 shadow-sm">
                  <div className="card-header bg-transparent border-0 pb-0">
                    <h5 className="mb-0 d-flex align-items-center">
                      <FaClock className="me-2" style={{ color: '#059e98' }} />
                      Recent Activity Timeline
                    </h5>
                  </div>
                  <div className="card-body p-0">
                    <div className="timeline-wrapper p-3">
                      {stats.recentActivity.map((activity, index) => (
                        <div key={activity.id} className="timeline-item mb-3">
                          <div className="d-flex">
                            <div className="timeline-marker">
                              <div className={`marker-dot ${activity.type}`}
                                style={{
                                  backgroundColor: 
                                    activity.type === 'seo' ? '#C99A3A' :
                                    activity.type === 'hotel' ? '#8B5CF6' :
                                    activity.type === 'package' ? '#F59E0B' :
                                    activity.type === 'blog' ? '#EC4899' : '#059e98'
                                }}
                              ></div>
                              {index !== stats.recentActivity.length - 1 && (
                                <div className="timeline-line"></div>
                              )}
                            </div>
                            <div className="ms-3 flex-grow-1">
                              <div className="d-flex justify-content-between align-items-start">
                                <h6 className="mb-1" style={{ fontSize: '0.9rem' }}>{activity.action}</h6>
                                <small className="text-muted">{activity.time}</small>
                              </div>
                              <div className="d-flex align-items-center">
                                <small className="text-muted me-2">
                                  <FaUser className="me-1" size={10} />
                                  {activity.user}
                                </small>
                                <span className={`badge ${activity.type}-badge`} style={{
                                  backgroundColor: 
                                    activity.type === 'seo' ? '#C99A3A20' :
                                    activity.type === 'hotel' ? '#8B5CF620' :
                                    activity.type === 'package' ? '#F59E0B20' :
                                    activity.type === 'blog' ? '#EC489920' : '#059e9820',
                                  color: 
                                    activity.type === 'seo' ? '#C99A3A' :
                                    activity.type === 'hotel' ? '#8B5CF6' :
                                    activity.type === 'package' ? '#F59E0B' :
                                    activity.type === 'blog' ? '#EC4899' : '#059e98'
                                }}>
                                  {activity.type}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Info Cards */}
            <div className="row g-3">
              <div className="col-lg-6">
                <div className="card modern-card border-0 shadow-sm">
                  <div className="card-header bg-transparent border-0 pb-0">
                    <h5 className="mb-0 d-flex align-items-center">
                      <FaChartBar className="me-2" style={{ color: '#059e98' }} />
                      Performance Insights
                    </h5>
                  </div>
                  <div className="card-body" style={{ fontSize: '0.8rem' }}>
                    <div className="row text-center">
                      <div className="col-3">
                        <div className="p-3">
                          <div className="display-6 fw-bold mb-2" style={{ fontSize: '1.2rem', color: '#059e98' }}>98.5%</div>
                          <small className="text-muted">Uptime</small>
                        </div>
                      </div>
                      <div className="col-3">
                        <div className="p-3">
                          <div className="display-6 fw-bold mb-2" style={{ fontSize: '1.2rem', color: '#28a745' }}>2.1s</div>
                          <small className="text-muted">Response Time</small>
                        </div>
                      </div>
                      <div className="col-3">
                        <div className="p-3">
                          <div className="display-6 fw-bold mb-2" style={{ fontSize: '1.2rem', color: '#ffc107' }}>0</div>
                          <small className="text-muted">Errors Today</small>
                        </div>
                      </div>
                      <div className="col-3">
                        <div className="p-3">
                          <div className="display-6 fw-bold mb-2" style={{ fontSize: '1.2rem', color: '#17a2b8' }}>95%</div>
                          <small className="text-muted">Satisfaction</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-lg-6">
                <div className="card modern-card border-0 shadow-sm">
                  <div className="card-header bg-transparent border-0 pb-0">
                    <h5 className="mb-0 d-flex align-items-center">
                      <FaExclamationTriangle className="me-2" style={{ color: '#059e98' }} />
                      Quick Tips & Updates
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-6">
                        <ul className="list-unstyled mb-0">
                          <li className="mb-2 d-flex align-items-center">
                            <FaCheckCircle className="me-2 text-success flex-shrink-0" />
                            <small>SEO meta descriptions under 160 chars</small>
                          </li>
                          <li className="mb-2 d-flex align-items-center">
                            <FaCheckCircle className="me-2 text-success flex-shrink-0" />
                            <small>Use WebP for hotel images</small>
                          </li>
                          <li className="d-flex align-items-center">
                            <FaCheckCircle className="me-2 text-success flex-shrink-0" />
                            <small>Update prices before seasons</small>
                          </li>
                        </ul>
                      </div>
                      <div className="col-6">
                        <ul className="list-unstyled mb-0">
                          <li className="mb-2 d-flex align-items-center">
                            <FaCalendarAlt className="me-2 text-primary flex-shrink-0" />
                            <small>Next backup: Tonight 2:00 AM</small>
                          </li>
                          <li className="mb-2 d-flex align-items-center">
                            <FaUserPlus className="me-2 text-info flex-shrink-0" />
                            <small>3 new users this week</small>
                          </li>
                          <li className="d-flex align-items-center">
                            <FaDownload className="me-2 text-success flex-shrink-0" />
                            <small>Update available v2.1.3</small>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <Footer />
        </div>
      </div>
    </>
  )
}